import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface CustomerOrder {
    data: never[];
    id: number;
    paid: boolean;
    cookName: string;
    mealType: string;
    cookEmail: string;
    packLevel: string;
    cookGender: string;
    kitchenName: string;
    paymentMode: string;
    totalAmount: number;
    auditTrailId: number | null;
    cookLastName: string;
    customerName: string;
    cookProfileId: number;
    customerEmail: string;
    customerPhone: string;
    orderFoodName: string;
    orderPlacedAt: string; // ISO date string
    orderQuantity: number;
    orderStatusId: number;
    userProfileId: number;
    customerGender: string;
    orderReference: string;
    orderRequestId: number | null;
    specialMention: string;
    orderStatusName: string;
    cookMobileNumber: string;
    customerLastName: string;
    expectedDeliveryAt: string; // ISO date string
    specialInstructions: string;
    firstName?: string
}


interface CustomerOrderState {
    data: CustomerOrder[];
    customerOrders: CustomerOrder
    getByIdData: CustomerOrder | null
    loading: boolean;
    error: string | null;
}

const PORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const URL = import.meta.env.VITE_API_FOOD_ORDER_URL;

const USERSPORT = import.meta.env.VITE_USERS_PORT;
const USERSURL = import.meta.env.VITE_API_USERS_URL;


export const fetchCustomerOrder = createAsyncThunk<CustomerOrder[], any, { rejectValue: string }>(
  'CustomerOrder/fetchCustomerOrder',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.post<{ data: CustomerOrder[] }>(
        '/get_all_customer_order',
        params ?? {},
        PORT,
        URL,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getByIdCustomerOrder = createAsyncThunk<
    CustomerOrder,
    { id: number },
    { rejectValue: string }
>(
    'getByIdCustomerOrder/customerOrder',
    async (params, { rejectWithValue }) => {
        try {
            const config = { params }
            const response: any = await apiService.get<{ customerOrder: CustomerOrder }>(
                '/get_by_id_customer_order',
                PORT,
                URL,
                config
            )

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'An error occurred'
            )
        }
    }
)





export const createCustomerOrder = createAsyncThunk<CustomerOrder, any, { rejectValue: string }>(
    'CustomerOrder/createCustomerOrder',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.post<{ data: CustomerOrder }>('create_customer_order', params, PORT, URL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);

export const updateCustomerOrder = createAsyncThunk<CustomerOrder, any, { rejectValue: string }>(
    'CustomerOrder/updateCustomerOrder',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.put<{ data: CustomerOrder }>('update_customer_order', params, PORT, URL);
            return response.data;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
        }
    }
);
export const getAllCustomersByAreaCodeId = createAsyncThunk<
    CustomerOrder,
    { areaCodeId: any },
    { rejectValue: string }
>(
    'CustomerOrder/getAllCustomersByAreaCodeId',
    async (params, { rejectWithValue }) => {
        try {
            const config = { params }
            const response: any = await apiService.get<{ customerOrder: CustomerOrder }>(
                '/get_all_customers_by_area_code_id',
                USERSPORT,
                USERSURL,
                config
            )

            return response.data;
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'An error occurred'
            )
        }
    }
)

export const customerOrderSlice = createSlice({
    name: 'CustomerOrder',
    initialState: {
        data: [],
        customerOrders: [],
        getByIdData: null,
        loading: false,
        error: null,
    } as unknown as CustomerOrderState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Diet Mapping Code
            .addCase(fetchCustomerOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerOrder.fulfilled, (state, action: PayloadAction<CustomerOrder[]>) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchCustomerOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })

            .addCase(getByIdCustomerOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getByIdCustomerOrder.fulfilled, (state, action) => {
                state.loading = false;
                state.customerOrders = action.payload;
            })

            .addCase(getByIdCustomerOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })



           
            .addCase(createCustomerOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCustomerOrder.fulfilled, (state, action: any) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(createCustomerOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
            .addCase(updateCustomerOrder.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCustomerOrder.fulfilled, (state, action: PayloadAction<CustomerOrder>) => {
                state.loading = false;
                state.data.push(action.payload);
            })
            .addCase(updateCustomerOrder.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? 'Unknown error';
            })
    },
});

export default customerOrderSlice.reducer;